import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Trash2, Check, AlertTriangle, X } from 'lucide-react';
import { useAccounts, useCreateJournalEntry } from '@/hooks/useJournal';
import { useContacts } from '@/hooks/useCrm';
import { extractError } from '@/api/client';
import { formatNumber } from '@/utils/currency';
import { JOURNAL_TYPE_LABELS, type JournalType } from '@/types';

interface Props {
  open:    boolean;
  onClose: () => void;
}

interface LineForm {
  accountId: string;
  /** Tiers — obligatoire sur un compte client / fournisseur 40x / 41x (lot 2, étape 2.4). */
  contactId: string;
  label:     string;
  debit:     string;
  credit:    string;
}

const emptyLine = (): LineForm => ({ accountId: '', contactId: '', label: '', debit: '', credit: '' });
const estCompteTiers = (code?: string) => !!code && (code.startsWith('40') || code.startsWith('41'));
const todayISO  = () => new Date().toISOString().slice(0, 10);
const suggestRef = (type: JournalType): string => `${type}-${new Date().getFullYear()}-001`;

/**
 * Journal Modal pixel-perfect CTO-FRONT-002.
 * Référence : design-journal-comptable-v1.html lignes 427-527.
 * - Header gradient var(--p-deep) → var(--p-dark)
 * - Lines table inline avec balance footer (Total Débit / Crédit / ✓ Équilibré)
 * - Validation SYSCOHADA temps réel
 */
export const JournalEntryDialog = ({ open, onClose }: Props) => {
  const [journalType, setJournalType] = useState<JournalType>('VE');
  const [reference, setReference]     = useState(suggestRef('VE'));
  const [entryDate, setEntryDate]     = useState(todayISO());
  const [description, setDescription] = useState('');
  const [lines, setLines]             = useState<LineForm[]>([emptyLine(), emptyLine()]);
  const [error, setError]             = useState<string | null>(null);

  const { data: accounts = [], isLoading: accountsLoading } = useAccounts();
  // Lot 2, étape 2.4 : tiers proposés sur les comptes clients / fournisseurs (prospects exclus).
  // Clients et fournisseurs chargés séparément (la liste est plafonnée à 100 par appel).
  const { data: clientsPage } = useContacts({ contactType: 'client', limit: 100 });
  const { data: fournisseursPage } = useContacts({ contactType: 'supplier', limit: 100 });
  const tiers = [...(clientsPage?.data ?? []), ...(fournisseursPage?.data ?? [])];
  const create = useCreateJournalEntry();

  useEffect(() => {
    if (open) {
      setJournalType('VE');
      setReference(suggestRef('VE'));
      setEntryDate(todayISO());
      setDescription('');
      setLines([emptyLine(), emptyLine()]);
      setError(null);
    }
  }, [open]);

  useEffect(() => { setReference(suggestRef(journalType)); }, [journalType]);

  // Fermeture sur Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const updateLine = (idx: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, ...patch };
      if (patch.debit  !== undefined && patch.debit  !== '') next.credit = '';
      if (patch.credit !== undefined && patch.credit !== '') next.debit  = '';
      return next;
    }));
  };

  const addLine    = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    let debit = 0, credit = 0;
    for (const l of lines) {
      debit  += parseFloat(l.debit  || '0');
      credit += parseFloat(l.credit || '0');
    }
    const ecart = Math.abs(debit - credit);
    const balanced = ecart < 0.001 && debit > 0;
    return { debit, credit, ecart, balanced };
  }, [lines]);

  const canSubmit =
    lines.length >= 2 &&
    lines.every((l) => l.accountId && l.label.trim() &&
      (!estCompteTiers(accounts.find((a) => a.id === l.accountId)?.code) || !!l.contactId) && (parseFloat(l.debit || '0') > 0 || parseFloat(l.credit || '0') > 0)) &&
    totals.balanced &&
    reference.trim() &&
    description.trim();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        journalType,
        reference:   reference.trim(),
        entryDate,
        description: description.trim(),
        lines: lines.map((l) => ({
          accountId:   l.accountId,
          contactId:   l.contactId || undefined,
          label:       l.label.trim(),
          debitCents:  Math.round(parseFloat(l.debit  || '0') * 100),
          creditCents: Math.round(parseFloat(l.credit || '0') * 100),
        })),
      });
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  if (!open) return null;

  return (
    <div className="ds-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ds-modal size-lg">
        {/* Header gradient */}
        <header className="ds-modal-header">
          <div>
            <div className="ds-modal-title">Nouvelle écriture comptable</div>
            <div className="ds-modal-sub">SYSCOHADA révisé · Journal Leadbeton CI</div>
          </div>
          <button type="button" className="ds-modal-close" onClick={onClose} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={onSubmit}>
          <div className="ds-modal-body">
            {error && (
              <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
                <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
                <div className="ds-alert-txt">{error}</div>
              </div>
            )}

            {/* En-tête : Date / Journal / Référence */}
            <div className="ds-form-row">
              <Field label="Date de l'écriture">
                <input
                  className="ds-fi-modal"
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  required
                />
              </Field>
              <Field label="Journal">
                <select
                  className="ds-fi-modal"
                  value={journalType}
                  onChange={(e) => setJournalType(e.target.value as JournalType)}
                >
                  {(Object.keys(JOURNAL_TYPE_LABELS) as JournalType[]).map((t) => (
                    <option key={t} value={t}>{t} — {JOURNAL_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </Field>
              <Field label="Référence">
                <input
                  className="ds-fi-modal mono"
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  required
                />
              </Field>
            </div>

            <div className="ds-fld" style={{ marginBottom: 16 }}>
              <label>Libellé général</label>
              <input
                className="ds-fi-modal"
                type="text"
                placeholder="Ex : Vente béton chantier Cocody – Kouassi BTP"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            {/* Lignes */}
            <div className="ds-lines-section">
              <div className="ds-lines-title">
                Lignes de l'écriture
                <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={addLine}>
                  <Plus size={12} /> Ajouter ligne
                </button>
              </div>

              <table className="ds-lines-table">
                <thead>
                  <tr>
                    <th style={{ width: '38%' }}>N° Compte SYSCOHADA</th>
                    <th>Intitulé / Libellé</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Débit (FCFA)</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Crédit (FCFA)</th>
                    <th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={idx}>
                      <td>
                        <select
                          className="ds-line-input mono"
                          value={line.accountId}
                          onChange={(e) => updateLine(idx, { accountId: e.target.value })}
                          disabled={accountsLoading}
                        >
                          <option value="">— Sélectionner —</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} · {a.label}</option>
                          ))}
                        </select>
                        {estCompteTiers(accounts.find((a) => a.id === line.accountId)?.code) && (
                          <select
                            className="ds-line-input"
                            style={{ marginTop: 4 }}
                            value={line.contactId}
                            onChange={(e) => updateLine(idx, { contactId: e.target.value })}
                            aria-label="Tiers (client ou fournisseur)"
                          >
                            <option value="">— Tiers (obligatoire) —</option>
                            {tiers.map((c) => (
                              <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          className="ds-line-input"
                          type="text"
                          value={line.label}
                          onChange={(e) => updateLine(idx, { label: e.target.value })}
                          placeholder="Libellé"
                        />
                      </td>
                      <td>
                        <input
                          className="ds-line-input num mono"
                          type="number" min="0" step="1"
                          value={line.debit}
                          onChange={(e) => updateLine(idx, { debit: e.target.value })}
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          className="ds-line-input num mono"
                          type="number" min="0" step="1"
                          value={line.credit}
                          onChange={(e) => updateLine(idx, { credit: e.target.value })}
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ds-del-btn"
                          disabled={lines.length <= 2}
                          onClick={() => removeLine(idx)}
                          aria-label="Supprimer ligne"
                          title={lines.length <= 2 ? 'Minimum 2 lignes' : 'Supprimer'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Balance footer */}
              <div className="ds-balance-footer">
                <div className="ds-bal-item">
                  <div className="ds-bal-lbl">Total Débit</div>
                  <div className="ds-bal-val">{formatNumber(totals.debit)}</div>
                </div>
                <div className="ds-bal-sep" />
                <div className="ds-bal-item">
                  <div className="ds-bal-lbl">Total Crédit</div>
                  <div className="ds-bal-val">{formatNumber(totals.credit)}</div>
                </div>
                <div className="ds-bal-sep" />
                <div className={`ds-bal-check ${totals.balanced ? 'ds-bal-ok' : 'ds-bal-err'}`}>
                  {totals.balanced ? (
                    <><Check size={14} /> Écriture équilibrée</>
                  ) : (
                    <><AlertTriangle size={14} /> {totals.debit === 0 && totals.credit === 0 ? 'Saisir les montants' : `Écart : ${formatNumber(totals.ecart)} FCFA`}</>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer modal */}
          <div className="ds-modal-footer">
            <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
            <button
              type="submit"
              className="ds-btn ds-btn-p"
              disabled={!canSubmit || create.isPending}
              style={{ opacity: !canSubmit || create.isPending ? 0.5 : 1, cursor: !canSubmit || create.isPending ? 'not-allowed' : 'pointer' }}
            >
              {create.isPending ? 'Création…' : '✓ Valider l\'écriture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="ds-fld">
    <label>{label}</label>
    {children}
  </div>
);
